import { FC, useCallback, useState } from 'react';

import { useFormikContext } from 'formik';
import { compact, debounce } from 'lodash';
import type { GroupBase, OptionsOrGroups } from 'react-select';
import { Input } from 'ui/components';
import {
  Autocomplete,
  PhoneNumber,
  Select,
  TagsField,
} from 'ui/components/form';
import { ACCOUNT_STATUS_SOURCE, SIZE } from 'utils/constants';
import { cn } from 'utils/helpers';
import { useGetAccountStatuses } from 'modules/account/settings/hooks';

import { useGetCompanies } from '../hooks';
import { LEAD_FORM } from './schema';

const LeadDetailsForm: FC = () => {
  const [companyQuery, setCompanyQuery] = useState('');

  const { data: companies } = useGetCompanies({ keyword: companyQuery });
  const { data: status } = useGetAccountStatuses(ACCOUNT_STATUS_SOURCE.LEAD);

  const {
    values,
    touched,
    errors,
    handleChange,
    handleBlur,
    isSubmitting,
    setFieldValue,
    setFieldError,
  } = useFormikContext<LEAD_FORM>();

  const companyItems = useCallback(() => {
    const filteredCompanies =
      companies?.data?.map((comp: any) => ({
        label: comp.name,
        value: comp.id,
      })) ?? [];

    return filteredCompanies;
  }, [companies]);

  const debouncedSearch = debounce((string) => setCompanyQuery(string), 500);

  const loadOptions = (
    inputValue: string,
    callback: (options: OptionsOrGroups<unknown, GroupBase<unknown>>[]) => void,
  ) => {
    debouncedSearch(inputValue);

    setTimeout(() => {
      //TODO: Type this properly
      callback(companyItems() as any);
    }, 500);
  };

  return (
    <div className={cn('grid grid-cols-2 gap-y-6 gap-x-5', 'pt-7')}>
      <Select
        className="col-span-2"
        textType={
          errors.accountStatusId && touched.accountStatusId
            ? 'danger'
            : 'default'
        }
        subText={
          errors.accountStatusId && touched.accountStatusId
            ? errors.accountStatusId
            : undefined
        }
        label="Status"
        onChange={(val) => setFieldValue('accountStatusId', val.value)}
        options={status?.map((s) => ({ label: s.name, value: s._id })) ?? []}
        size={SIZE.Small}
        name="status"
        placeholder="Select"
        disabled={isSubmitting}
        defaultValue={values.accountStatusId as string}
        required
      />
      <Input
        className="w-full"
        type="text"
        name="firstName"
        size={SIZE.Medium}
        textType={errors.firstName && touched.firstName ? 'danger' : 'default'}
        onChange={handleChange}
        onBlur={handleBlur}
        value={values.firstName}
        placeholder="Enter your first name"
        subText={
          errors.firstName && touched.firstName ? errors.firstName : undefined
        }
        label="First Name"
        required
        disabled={isSubmitting}
      />
      <Input
        className="w-full"
        type="text"
        name="lastName"
        size={SIZE.Medium}
        textType={errors.lastName && touched.lastName ? 'danger' : 'default'}
        onChange={handleChange}
        onBlur={handleBlur}
        value={values.lastName ?? ''}
        placeholder="Enter your last name"
        subText={
          errors.lastName && touched.lastName ? errors.lastName : undefined
        }
        label="Last Name"
        disabled={isSubmitting}
      />
      <Input
        className="w-full"
        type="text"
        name="emailAddress"
        size={SIZE.Medium}
        textType={errors.emailAddress ? 'danger' : 'default'}
        onChange={handleChange}
        onBlur={handleBlur}
        value={values.emailAddress}
        placeholder="Enter your email address"
        subText={errors.emailAddress ? errors.emailAddress : undefined}
        required
        label="Email"
        disabled={isSubmitting}
      />
      <PhoneNumber
        className="w-full"
        name="mobileNumber"
        size={SIZE.Medium}
        textType={
          errors.mobileNumber && touched.mobileNumber ? 'danger' : 'default'
        }
        onChange={setFieldValue}
        value={values.mobileNumber ?? ''}
        subText={
          errors.mobileNumber && touched.mobileNumber
            ? errors.mobileNumber
            : undefined
        }
        label="Mobile Number"
        disabled={isSubmitting}
      />
      <Autocomplete
        className="w-full"
        state={errors.companyId && touched.companyId ? 'danger' : undefined}
        subText={
          errors.companyId && touched.companyId ? errors.companyId : undefined
        }
        label="Company"
        size={SIZE.Medium}
        name="companyName"
        placeholder="Select"
        defaultInputValue={values.companyName ?? ''}
        onChange={(val: any) => {
          setFieldValue('companyId', val.__isNew__ ? null : val.value);
          setFieldValue('companyName', val.label);
        }}
        loadOptions={loadOptions}
        isCreatable
      />
      <PhoneNumber
        className="w-full"
        name="businessNumber"
        size={SIZE.Medium}
        textType={
          errors.businessNumber && touched.businessNumber ? 'danger' : 'default'
        }
        onChange={setFieldValue}
        value={values.businessNumber ?? ''}
        placeholder="Enter your landline number"
        subText={
          errors.businessNumber && touched.businessNumber
            ? errors.businessNumber
            : undefined
        }
        label="Business Number"
        disabled={isSubmitting}
      />
      <Input
        className="w-full"
        type="text"
        name="other"
        size={SIZE.Medium}
        textType={errors.other && touched.other ? 'danger' : 'default'}
        onChange={handleChange}
        onBlur={handleBlur}
        value={values.other}
        placeholder="Other"
        subText={errors.other && touched.other ? errors.other : undefined}
        label="Other"
        disabled={isSubmitting}
      />
      <Input
        className="w-full"
        type="text"
        name="nameToPrintOnCheck"
        size={SIZE.Medium}
        textType={
          errors.nameToPrintOnCheck && touched.nameToPrintOnCheck
            ? 'danger'
            : 'default'
        }
        onChange={handleChange}
        onBlur={handleBlur}
        value={values.nameToPrintOnCheck}
        placeholder="Name"
        subText={
          errors.nameToPrintOnCheck && touched.nameToPrintOnCheck
            ? errors.nameToPrintOnCheck
            : undefined
        }
        label="Name to print on check"
        disabled={isSubmitting}
      />
      <Input
        className="col-span-2 w-full"
        type="text"
        name="website"
        size={SIZE.Medium}
        textType={errors.website && touched.website ? 'danger' : 'default'}
        onChange={handleChange}
        onBlur={handleBlur}
        value={values.website}
        placeholder="Website"
        subText={errors.website && touched.website ? errors.website : undefined}
        label="Website"
        disabled={isSubmitting}
      />
      <TagsField
        className="col-span-2 w-full"
        onChange={(val) =>
          setFieldValue(
            'tagId',
            val?.map((tag) => tag.value),
          )
        }
        defaultValue={compact(values.tagId)}
        setError={(err) => {
          if (err) {
            setFieldError(
              'tagId',
              'Tags cannot have spaces and special characters.',
            );
          }
        }}
      />
    </div>
  );
};

export default LeadDetailsForm;
